/**
 * Microsoft Entra sign-in via the shared "JHR Engineering Tools (workbench)"
 * app registration. Who can sign in is controlled by the Entra group
 * sec-app-engineering-tools-users, not by this code.
 *
 * Served under workbench.juncosracing.com/vrig, the SPA reuses the shared
 * Workbench MSAL session silently and sends its Entra ID token as
 * `Authorization: Bearer` on every /api call (see api/httpBootstrap.ts); the
 * backend verifies issuer/audience/signature. Dev builds bypass the gate and
 * use the vite proxy.
 */
import { PublicClientApplication, type AccountInfo } from "@azure/msal-browser";

const TENANT_ID = "73c41499-4134-4c4a-8dce-7ddfcca2606b";
const CLIENT_ID = "dfa75e5f-66e5-49f2-818f-b2a670e8288e";

export const msal = new PublicClientApplication({
  auth: {
    clientId: CLIENT_ID,
    authority: `https://login.microsoftonline.com/${TENANT_ID}`,
    redirectUri: `${window.location.origin}/`,
  },
  cache: { cacheLocation: "localStorage" },
});

export const LOGIN_REQUEST = { scopes: ["User.Read"] };

export async function resolveAccount(): Promise<AccountInfo | null> {
  await msal.initialize();
  const result = await msal.handleRedirectPromise();
  if (result?.account) {
    msal.setActiveAccount(result.account);
    return result.account;
  }
  const account = msal.getActiveAccount() ?? msal.getAllAccounts()[0] ?? null;
  if (account) msal.setActiveAccount(account);
  return account;
}

export async function getIdToken(forceRefresh = false): Promise<string> {
  const account = msal.getActiveAccount();
  if (!account) throw new Error("not signed in");
  try {
    const result = await msal.acquireTokenSilent({ ...LOGIN_REQUEST, account, forceRefresh });
    return result.idToken;
  } catch {
    await msal.acquireTokenRedirect({ ...LOGIN_REQUEST, account });
    throw new Error("redirecting to sign-in");
  }
}
